import { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import { listAuditLog, auditLogCsvUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  History,
  PlusCircle,
  CheckCircle2,
  Camera,
  MessageSquare,
  CalendarClock,
  Package,
  UserCog,
} from "lucide-react";

const ICONS = {
  create: PlusCircle,
  status_change: CheckCircle2,
  photo_upload: Camera,
  note: MessageSquare,
  reschedule: CalendarClock,
  part_consume: Package,
  role_change: UserCog,
};

const COLORS = {
  create: "bg-blue-50 text-blue-700",
  status_change: "bg-emerald-50 text-emerald-700",
  photo_upload: "bg-orange-50 text-orange-700",
  note: "bg-zinc-100 text-zinc-700",
  reschedule: "bg-yellow-50 text-yellow-800",
  part_consume: "bg-purple-50 text-purple-700",
  role_change: "bg-red-50 text-red-700",
};

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [entity, setEntity] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    const params = { limit: 200 };
    if (entity !== "all") params.entity = entity;
    listAuditLog(params).then(setEntries);
  }, [entity]);

  const filtered = entries.filter((e) => {
    if (!q) return true;
    const blob = `${e.action} ${e.entity} ${e.entity_id} ${e.by_name || ""} ${JSON.stringify(e.meta || {})}`.toLowerCase();
    return blob.includes(q.toLowerCase());
  });

  return (
    <Layout title="Audit Log" breadcrumbs={["Admin", "Audit Log"]}>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input
          data-testid="audit-search"
          placeholder="Search action, user, entity, meta…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="rounded-sm h-10 flex-1 bg-white border-zinc-300"
        />
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger data-testid="audit-entity-filter" className="w-full sm:w-48 rounded-sm h-10 bg-white border-zinc-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            <SelectItem value="work_order">Work Orders</SelectItem>
            <SelectItem value="user">Users</SelectItem>
          </SelectContent>
        </Select>
        <a
          data-testid="audit-csv-btn"
          href={auditLogCsvUrl()}
          className="inline-flex items-center justify-center gap-1 h-10 px-3 rounded-sm border border-zinc-300 bg-white hover:bg-zinc-50 text-xs font-bold uppercase tracking-wider"
        >
          CSV
        </a>
      </div>

      <div className="engineered-card">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500 flex flex-col items-center gap-2">
            <History className="h-8 w-8 text-zinc-300" />
            No audit entries yet.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filtered.map((e) => {
              const Icon = ICONS[e.action] || History;
              const accent = COLORS[e.action] || "bg-zinc-100 text-zinc-700";
              return (
                <div
                  key={e.id}
                  data-testid={`audit-${e.id}`}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-zinc-50"
                >
                  <div className={`h-8 w-8 rounded-sm flex items-center justify-center shrink-0 ${accent}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <b>{e.by_name || "System"}</b>{" "}
                      <span className="text-zinc-600">
                        {e.action.replace("_", " ")}
                      </span>{" "}
                      <span className="font-mono text-xs text-zinc-500">
                        {e.entity}:{e.entity_id.slice(0, 12)}
                      </span>
                    </div>
                    {e.meta && Object.keys(e.meta).length > 0 && (
                      <div className="text-xs text-zinc-500 font-mono mt-0.5">
                        {Object.entries(e.meta).map(([k, v]) => `${k}=${v}`).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 whitespace-nowrap pt-1">
                    {new Date(e.at).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
